import { useCallback, useEffect, useState } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { Button, Stack, ListItemText } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fCurrencyRupees } from 'src/utils/format-number';
import { hasPermission } from 'src/utils/has-permissions';
import { formatFactorableRange } from 'src/utils/cheque-factoring-helpers';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getFactoringBatches, getFactorableSummary } from 'src/redux/slices/cheque-factoring';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  FACTORING_BATCH_COMPLETED,
  FactoringBatchStatus,
  IFactoringBatch,
} from 'src/types/cheque-factoring';

import BatchTableRow from '../batch-table-row';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: FactoringBatchStatus.SUBMITTED, label: 'Submitted' },
  { value: FactoringBatchStatus.ADVANCE_RECEIVED, label: 'Advance Received' },
  { value: FACTORING_BATCH_COMPLETED, label: 'Completed' },
];

const TABLE_HEAD = [
  { id: 'batchDate', label: 'Batch Date', align: 'left', primary: true },
  { id: 'cheques', label: 'Cheques', align: 'left', primary: true },
  { id: 'totalAmount', label: 'Total', align: 'right', primary: true },
  { id: 'advanceAmount', label: 'Advance (80%)', align: 'right', primary: true },
  { id: 'status', label: 'Status', align: 'left', primary: true },
  { id: '', primary: true },
];

export default function BatchListView() {
  const dispatch = useAppDispatcher();
  const { batches, factorableSummary } = useAppSelector((state) => state.chequeFactoring);
  const { user: authUser } = useAuthContext();

  const canReadCandidates = hasPermission(
    authUser,
    'any',
    AuthPermissions.READ_FACTORING_CANDIDATES,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const settings = useSettingsContext();
  const table = useTable({ defaultRowsPerPage: 25, defaultDense: true });
  const denseHeight = table.dense ? 60 : 80;

  const [statusTab, setStatusTab] = useState<string>('all');

  const fetchBatches = useCallback(() => {
    dispatch(
      getFactoringBatches({
        page: table.page,
        limit: table.rowsPerPage,
        status: statusTab === 'all' ? null : statusTab,
        sortBy: 'batchDate',
        sort: 'desc',
      })
    );
  }, [dispatch, table.page, table.rowsPerPage, statusTab]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if (canReadCandidates) {
      dispatch(getFactorableSummary());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadCandidates]);

  const handleFilterStatus = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      setStatusTab(newValue);
      table.onResetPage();
    },
    [table]
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Factoring Batches"
        links={[{ name: 'Cheque Factoring' }, { name: 'Batches' }]}
        action={
          <Stack direction="row" alignItems="center" gap={2}>
            {canReadCandidates && (
              <ListItemText
                primary="Company Headroom"
                secondary={
                  factorableSummary.status === 'loading'
                    ? 'Loading…'
                    : fCurrencyRupees(factorableSummary.data?.companyHeadroomInCents ?? 0)
                }
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            )}

            {canReadCandidates && (
              <ListItemText
                primary="Factorable Now"
                secondary={
                  factorableSummary.status === 'loading'
                    ? 'Loading…'
                    : formatFactorableRange(factorableSummary.data)
                }
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            )}

            {canReadCandidates && (
              <Button
                component={RouterLink}
                href={paths.dashboard.chequeFactoring.candidates}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Batch
              </Button>
            )}
          </Stack>
        }
        sx={{ mb: { xs: 3 } }}
      />

      <Card>
        <Tabs
          value={statusTab}
          onChange={handleFilterStatus}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {STATUS_OPTIONS.map((tab) => (
            <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom headLabel={TABLE_HEAD} rowCount={batches.items.length} />

              <TableBody>
                {batches.status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((_, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {batches.items.map((row: IFactoringBatch) => (
                      <BatchTableRow key={row._id} row={row} />
                    ))}
                  </>
                )}

                {batches.status !== 'failure' && batches.count === 0 && (
                  <TableNoData
                    message="No factoring batches found"
                    notFound={batches.count === 0}
                  />
                )}

                {batches.status === 'failure' && <TableErrorData notFound error={batches.error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense
          count={batches.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
